import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  FaPlus,
  FaSearch,
  FaTruck,
  FaEdit,
  FaTrash,
  FaCheck,
  FaBan
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import ProveedorFormModal from '../components/egresos/ProveedorFormModal';
import ConfirmModal from '../components/shared/ConfirmModal';
import { GET_PROVEEDORES, ELIMINAR_PROVEEDOR } from '../graphql/proveedores';
import './InventarioPage.css';
import './Proveedores.css';

/*
  Usage:
  <Route path="/proveedores" element={<PrivateRoute><Proveedores /></PrivateRoute>} />
*/

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(value || 0);

const CATEGORIAS_PROVEEDOR = [
  'insumos',
  'servicios',
  'tecnologia',
  'alimentos',
  'limpieza',
  'mantenimiento',
  'otro'
];

const Proveedores = () => {
  const [busqueda, setBusqueda] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [mostrarEliminarModal, setMostrarEliminarModal] = useState(false);
  const [proveedorAccion, setProveedorAccion] = useState(null);

  const { data, loading, refetch } = useQuery(GET_PROVEEDORES, {
    variables: {
      activo: mostrarInactivos ? null : true,
      busqueda: busqueda || null,
      categoria: categoriaFiltro || null
    },
    fetchPolicy: 'network-only'
  });

  const [eliminarProveedor] = useMutation(ELIMINAR_PROVEEDOR, {
    onCompleted: () => {
      toast.success('Proveedor eliminado correctamente');
      refetch();
    },
    onError: (err) => toast.error(err.message)
  });

  const proveedores = data?.proveedores || [];

  const estadisticas = useMemo(() => {
    const total = proveedores.length;
    const activos = proveedores.filter((p) => p.activo).length;
    const totalCompras = proveedores.reduce((acc, p) => acc + (p.total_compras || 0), 0);
    const totalGastos = proveedores.reduce((acc, p) => acc + (p.total_gastos || 0), 0);
    return { total, activos, totalCompras, totalGastos };
  }, [proveedores]);

  const handleNuevo = () => {
    setProveedorSeleccionado(null);
    setMostrarModal(true);
  };

  const handleEditar = (proveedor) => {
    setProveedorSeleccionado(proveedor);
    setMostrarModal(true);
  };

  const handleEliminar = (proveedor) => {
    setProveedorAccion(proveedor);
    setMostrarEliminarModal(true);
  };

  const ejecutarEliminar = () => {
    if (!proveedorAccion) return;
    eliminarProveedor({ variables: { id: parseInt(proveedorAccion.id) } });
    setMostrarEliminarModal(false);
    setProveedorAccion(null);
  };

  const handleCerrarModal = () => {
    setMostrarModal(false);
    setProveedorSeleccionado(null);
    refetch();
  };

  const getCondicionLabel = (condicion) => {
    const labels = {
      contado: 'Contado',
      credito_15: '15 dias',
      credito_30: '30 dias',
      credito_60: '60 dias',
      credito_90: '90 dias'
    };
    return labels[condicion] || condicion || '—';
  };

  return (
      <div className="inventario-page">
        {/* Header */}
        <div className="inventario-header">
          <div className="header-titulo">
            <FaTruck className="header-icon" />
            <div>
              <h1>Proveedores</h1>
              <p>Administra los proveedores del hotel</p>
            </div>
          </div>

          <div className="header-actions">
            <button className="btn-primary" onClick={handleNuevo}>
              <FaPlus /> Nuevo Proveedor
            </button>
          </div>
        </div>

        {/* Estadisticas */}
        <div className="inventario-stats">
          <div className="stat-card">
            <div className="stat-value">{estadisticas.total}</div>
            <div className="stat-label">Total Proveedores</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{estadisticas.activos}</div>
            <div className="stat-label">Activos</div>
          </div>
          <div className="stat-card">
            <div className="stat-value proveedor-stat-currency">{formatCurrency(estadisticas.totalCompras)}</div>
            <div className="stat-label">Total en Compras</div>
          </div>
          <div className="stat-card">
            <div className="stat-value proveedor-stat-currency">{formatCurrency(estadisticas.totalGastos)}</div>
            <div className="stat-label">Total en Gastos</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="inventario-filtros">
          <div className="filtro-busqueda">
            <FaSearch />
            <input
              type="text"
              placeholder="Buscar por nombre, NIT o contacto..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>

          <select
            className="filtro-select"
            value={categoriaFiltro}
            onChange={(e) => setCategoriaFiltro(e.target.value)}
          >
            <option value="">Todas las categorias</option>
            {CATEGORIAS_PROVEEDOR.map((cat) => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>

          <label className="filtro-checkbox">
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
            />
            Mostrar inactivos
          </label>
        </div>

        {/* Tabla */}
        <div className="inventario-tabla-container">
          {loading ? (
            <div className="loading-state">Cargando proveedores...</div>
          ) : proveedores.length === 0 ? (
            <div className="empty-state">
              <FaTruck />
              <p>No hay proveedores registrados</p>
              <button className="btn-primary" onClick={handleNuevo}>
                <FaPlus /> Agregar primer proveedor
              </button>
            </div>
          ) : (
            <table className="inventario-tabla">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>NIT</th>
                  <th>Contacto</th>
                  <th>Condiciones Pago</th>
                  <th>Total Compras</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {proveedores.map((proveedor) => (
                  <tr key={proveedor.id} className={!proveedor.activo ? 'row-inactiva' : ''}>
                    <td>
                      <div className="item-nombre">
                        <strong>{proveedor.nombre}</strong>
                        {proveedor.nombre_comercial && proveedor.nombre_comercial !== proveedor.nombre && (
                          <small>{proveedor.nombre_comercial}</small>
                        )}
                        {proveedor.categoria && (
                          <span className="proveedor-categoria-tag">{proveedor.categoria}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="codigo">
                        {proveedor.nit
                          ? `${proveedor.nit}${proveedor.digito_verificacion ? `-${proveedor.digito_verificacion}` : ''}`
                          : '—'}
                      </span>
                    </td>
                    <td>
                      <div className="proveedor-contacto">
                        {proveedor.contacto_nombre && (
                          <span className="contacto-nombre">{proveedor.contacto_nombre}</span>
                        )}
                        {proveedor.contacto_telefono && (
                          <span className="contacto-info">{proveedor.contacto_telefono}</span>
                        )}
                        {proveedor.contacto_email && (
                          <span className="contacto-info contacto-email">{proveedor.contacto_email}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="proveedor-condiciones">
                        <span>{getCondicionLabel(proveedor.condiciones_pago)}</span>
                        {proveedor.dias_credito > 0 && (
                          <small className="dias-credito">{proveedor.dias_credito} dias credito</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <strong>{formatCurrency(proveedor.total_compras)}</strong>
                    </td>
                    <td>
                      {proveedor.activo ? (
                        <span className="estado-badge estado-activo">
                          <FaCheck /> Activo
                        </span>
                      ) : (
                        <span className="estado-badge estado-inactivo">
                          <FaBan /> Inactivo
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="acciones">
                        <button
                          className="btn-accion btn-editar"
                          onClick={() => handleEditar(proveedor)}
                          title="Editar proveedor"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn-accion btn-eliminar"
                          onClick={() => handleEliminar(proveedor)}
                          title="Eliminar proveedor"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal */}
        {mostrarModal && (
          <ProveedorFormModal
            isOpen={mostrarModal}
            onClose={handleCerrarModal}
            proveedor={proveedorSeleccionado}
          />
        )}

        <ConfirmModal
          isOpen={mostrarEliminarModal}
          onClose={() => { setMostrarEliminarModal(false); setProveedorAccion(null); }}
          onConfirm={ejecutarEliminar}
          title="Eliminar Proveedor"
          message={`¿Deseas eliminar el proveedor "${proveedorAccion?.nombre}"? Esta accion no se puede deshacer.`}
          variant="danger"
          confirmText="Eliminar"
        />
      </div>
  );
};

export default Proveedores;
